/**
 * PR-V0b — R3F glue for `cameraApi.ts`.
 *
 * # Why this module exists
 *
 * `cameraApi.ts` is intentionally framework-agnostic — pure math +
 * ref bindings, no React or Three. The actual R3F-side wiring (read
 * the live `OrthographicCamera` into `cameraStateRef`, push tweened
 * mutations back, keep `viewportSizeRef` fresh) lives here so the
 * camera-API tests can stay pure-JS while the production path runs
 * inside `<Canvas>`.
 *
 * # Two responsibilities
 *
 *   1. **Snapshot bridge** (`<CameraApiBinder>`): a `useFrame` hook
 *      that mirrors the THREE camera state into the supplied
 *      `cameraStateRef` + `viewportSizeRef` every frame. Other code
 *      (e.g. `createStageCameraApi`) reads these refs synchronously
 *      so calls like `getCamera()` or the bounds-fit math always see
 *      the latest pan/zoom state.
 *
 *   2. **Tween driver** (`useCameraTweenDriver`): owns the per-frame
 *      interpolation of pending camera tweens. Callers schedule a
 *      tween via the shared `pendingTweenRef`; the driver runs the
 *      animation, writes intermediate `OrthographicCamera.position` +
 *      `.zoom` values, calls `invalidate()` while active, and clears
 *      the ref when complete.
 *
 * # Coordinate convention
 *
 * `cameraStateRef.current.zoom` is **CSS pixels per world unit**. The
 * THREE OrthographicCamera's `.zoom` property is a multiplier on the
 * frustum, so the conversion is:
 *
 *   pixelsPerWorld = ortho.zoom * size.width / (ortho.right - ortho.left)
 *
 * The frustum width `(right - left)` is treated as a fixed-on-fit
 * quantity: the existing fit-view path in `Scene3DStage.tsx`
 * recomputes it whenever the entity feed loads (so the initial paint
 * frames the drawing). Subsequent zoom mutations only adjust
 * `ortho.zoom`, not the frustum extents — keeping per-frame math
 * cheap and matching the legacy SVG-stage's scaling discipline.
 */

import * as React from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

import type { CameraStateSnapshot } from "./cameraApi";
import {
  pixelsPerWorldFromOrtho,
  orthoZoomFromPixelsPerWorld,
} from "./orthoZoomConversion";

// Re-export the pure conversion helpers (relocated to `orthoZoomConversion.ts`
// so framework-light modules can share them without pulling React / R3F in).
// Existing callers + tests keep importing them from `cameraApiBinder`.
export {
  pixelsPerWorldFromOrtho,
  orthoZoomFromPixelsPerWorld,
} from "./orthoZoomConversion";

/**
 * Tween descriptor written by `setCameraTween` and consumed by the
 * per-frame tween driver. The driver clears the field when the
 * animation completes (i.e. `t >= 1`).
 */
export interface PendingCameraTween {
  /** World-space target centre + pixelsPerWorld zoom. */
  target: CameraStateSnapshot;
  /** Snapshot captured at tween start (interpolation source). */
  origin: CameraStateSnapshot;
  /** Performance.now() timestamp when the tween was scheduled. */
  startedAt: number;
  /** Total duration in ms (default = `CAMERA_TWEEN_DURATION_MS`). */
  durationMs: number;
  /** Called once after the camera is snapped to the exact target at t >= 1. */
  onComplete?: () => void;
}

export interface CameraApiBinderProps {
  /** Mutable ref shared with `cameraApi.ts`. Updated every frame. */
  readonly cameraStateRef: React.MutableRefObject<CameraStateSnapshot>;
  /** Canvas CSS-pixel size; updated every frame. */
  readonly viewportSizeRef: React.MutableRefObject<{
    width: number;
    height: number;
  }>;
  /** Pending tween slot; the driver mutates the camera until cleared. */
  readonly pendingTweenRef: React.MutableRefObject<PendingCameraTween | null>;
}

/**
 * Smoothstep easing — `3t² - 2t³`. Matches the AutoCAD ZOOM tween
 * curve closely enough that user-visible motion feels native.
 */
function smoothstep(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}

/**
 * R3F-side binder: keeps `cameraStateRef` + `viewportSizeRef` in sync
 * with the live OrthographicCamera every frame, and runs any pending
 * tween. Returns `null` — contributes no THREE objects to the scene
 * tree.
 *
 * Mount inside `<Canvas>`. Multiple binders are safe (they all read
 * the same camera and write to their own refs).
 */
export function CameraApiBinder(props: CameraApiBinderProps): null {
  const { camera, size, invalidate } = useThree((state) => ({
    camera: state.camera,
    size: state.size,
    invalidate: state.invalidate,
  }));

  useFrame(() => {
    const ortho = camera as THREE.OrthographicCamera;
    if (!ortho.isOrthographicCamera) return;

    // ── Tween step (runs before the snapshot read so the snapshot
    // already reflects the new frame's camera state) ──────────────
    const pending = props.pendingTweenRef.current;
    if (pending) {
      const now =
        typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now();
      const elapsed = now - pending.startedAt;
      const raw =
        pending.durationMs > 0 ? elapsed / pending.durationMs : 1;
      const t = smoothstep(raw);
      const cx =
        pending.origin.centerX +
        (pending.target.centerX - pending.origin.centerX) * t;
      const cy =
        pending.origin.centerY +
        (pending.target.centerY - pending.origin.centerY) * t;
      const z =
        pending.origin.zoom +
        (pending.target.zoom - pending.origin.zoom) * t;

      const orthoZoom = orthoZoomFromPixelsPerWorld(z, ortho, size);
      ortho.position.x = cx;
      ortho.position.y = cy;
      ortho.zoom = orthoZoom;
      ortho.updateProjectionMatrix();

      if (raw >= 1) {
        // Final snap to exact target so we don't accumulate
        // smoothstep-residual drift over many tweens.
        ortho.position.x = pending.target.centerX;
        ortho.position.y = pending.target.centerY;
        ortho.zoom = orthoZoomFromPixelsPerWorld(
          pending.target.zoom,
          ortho,
          size,
        );
        ortho.updateProjectionMatrix();
        props.pendingTweenRef.current = null;
        // Fire after the ref is cleared so any onComplete-scheduled tween
        // is not immediately overwritten by this completion path.
        pending.onComplete?.();
      } else {
        // Keep the demand frameloop driving until the tween finishes.
        invalidate();
      }
    }

    // ── Snapshot bridge ───────────────────────────────────────────
    props.viewportSizeRef.current = {
      width: size.width,
      height: size.height,
    };
    props.cameraStateRef.current = {
      centerX: ortho.position.x,
      centerY: ortho.position.y,
      zoom: pixelsPerWorldFromOrtho(ortho, size),
    };
  });

  return null;
}
